if (!customElements.get('before-after-slider')) {
  customElements.define(
    'before-after-slider',
    class BeforeAfterSlider extends HTMLElement {
      constructor() {
        super();
        this.container = this.querySelector('.before-after-slider__container');
        this.imageWrapper = this.querySelector('.before-after-slider__image-wrapper');
        this.beforeImage = this.querySelector('.before-after-slider__image--before');
        this.afterImage = this.querySelector('.before-after-slider__image--after');
        this.divider = this.querySelector('.before-after-slider__divider');
        this.handle = this.querySelector('.before-after-slider__handle');

        this.isDragging = false;
        this.currentPosition = 50; // Default to 50%

        // Get initial position from data attribute or default to 50
        const initialPosition = parseFloat(this.dataset.initialPosition) || 50;
        this.setPosition(initialPosition);

        this.init();
      }

      init() {
        if (!this.container || !this.divider || !this.beforeImage || !this.afterImage) {
          return;
        }

        // Bind methods for cleanup
        this.boundHandleMouseDown = this.handleMouseDown.bind(this);
        this.boundHandleMouseMove = this.handleMouseMove.bind(this);
        this.boundHandleMouseUp = this.handleMouseUp.bind(this);
        this.boundHandleTouchStart = this.handleTouchStart.bind(this);
        this.boundHandleTouchMove = this.handleTouchMove.bind(this);
        this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);

        // Mouse events
        this.divider.addEventListener('mousedown', this.boundHandleMouseDown);
        document.addEventListener('mousemove', this.boundHandleMouseMove);
        document.addEventListener('mouseup', this.boundHandleMouseUp);

        // Touch events
        this.divider.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false });
        document.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false });
        document.addEventListener('touchend', this.boundHandleTouchEnd);

        // Keyboard navigation
        this.divider.addEventListener('keydown', this.boundHandleKeyDown);

        // Prevent image dragging
        this.beforeImage.addEventListener('dragstart', (e) => e.preventDefault());
        this.afterImage.addEventListener('dragstart', (e) => e.preventDefault());

        // Handle resize
        this.resizeObserver = new ResizeObserver(() => {
          this.setPosition(this.currentPosition);
        });
        this.resizeObserver.observe(this.container);

        // Mark as initialized to show text blocks
        this.classList.add('before-after-slider--initialized');
      }

      handleMouseDown(e) {
        e.preventDefault();
        this.isDragging = true;
        this.divider.classList.add('before-after-slider__divider--dragging');
        this.updatePosition(e.clientX);
      }

      handleMouseMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();
        this.updatePosition(e.clientX);
      }

      handleMouseUp(e) {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.divider.classList.remove('before-after-slider__divider--dragging');
      }

      handleTouchStart(e) {
        e.preventDefault();
        this.isDragging = true;
        this.divider.classList.add('before-after-slider__divider--dragging');
        const touch = e.touches[0];
        this.updatePosition(touch.clientX);
      }

      handleTouchMove(e) {
        if (!this.isDragging) return;
        e.preventDefault();
        const touch = e.touches[0];
        this.updatePosition(touch.clientX);
      }

      handleTouchEnd(e) {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.divider.classList.remove('before-after-slider__divider--dragging');
      }

      handleKeyDown(e) {
        const step = 5; // Percentage step for keyboard navigation
        let newPosition = this.currentPosition;

        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            newPosition = Math.max(0, this.currentPosition - step);
            break;
          case 'ArrowRight':
            e.preventDefault();
            newPosition = Math.min(100, this.currentPosition + step);
            break;
          case 'Home':
            e.preventDefault();
            newPosition = 0;
            break;
          case 'End':
            e.preventDefault();
            newPosition = 100;
            break;
          default:
            return;
        }

        this.setPosition(newPosition);
      }

      updatePosition(clientX) {
        if (!this.container) return;

        const rect = this.container.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

        this.setPosition(percentage);
      }

      setPosition(percentage) {
        this.currentPosition = percentage;

        if (this.divider) {
          this.divider.style.left = `${percentage}%`;
          this.divider.setAttribute('aria-valuenow', Math.round(percentage));
        }

        if (this.afterImage) {
          this.afterImage.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
        }

        // Also clip the before image to show only the left side
        if (this.beforeImage) {
          this.beforeImage.style.clipPath = `inset(0 0 0 ${percentage}%)`;
        }
      }

      disconnectedCallback() {
        // Cleanup event listeners
        if (this.divider) {
          this.divider.removeEventListener('mousedown', this.boundHandleMouseDown);
          this.divider.removeEventListener('touchstart', this.boundHandleTouchStart);
          this.divider.removeEventListener('keydown', this.boundHandleKeyDown);
        }
        document.removeEventListener('mousemove', this.boundHandleMouseMove);
        document.removeEventListener('mouseup', this.boundHandleMouseUp);
        document.removeEventListener('touchmove', this.boundHandleTouchMove);
        document.removeEventListener('touchend', this.boundHandleTouchEnd);

        if (this.resizeObserver && this.container) {
          this.resizeObserver.unobserve(this.container);
        }
      }
    }
  );
}
